import { useState, useEffect } from "react";
import { Card, Chip } from "@mui/material";
import { handleAxiosError } from "app/services/app.error.handler";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { getDateInFormat, getEndDateFromAnyMonth, getStartDateFromAnyMonth } from "utils/dateUtil";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useDispatch, useSelector } from "react-redux";
import { loadEffortSummary } from "app/services/member.service";
import {
  checkUserHasBdmRole,
  getBdmClients,
  readSessionCurrentClient,
} from "app/services/app.state.service";
import { EffortFilterBar } from "app/components/common/EffortFilterBar";
import { toClientListOptions } from "utils/clientOptions";
import { ALL_CLIENTS } from "AppConstants";
import { reportActions } from "store/report-slice";
import { ProfileTooltip } from "../header/Header";

const EffortSummaryView = () => {
  const dispatch = useDispatch();
  const [isCalled, setIsCalled] = useState(false);
  // Client scope for the panel.
  // @since NEXT_VERSION
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(ALL_CLIENTS);

  // A BDM's accounts are the ones naming them as relationship manager, not the session's client
  // list, which a BDM does not have. HRMS-116 point 3: they see reports for the clients they are
  // assigned to. @since NEXT_VERSION
  const isBdm = checkUserHasBdmRole();

  useEffect(() => {
    if (isBdm) {
      let cancelled = false;
      getBdmClients()
        .then((list) => {
          if (!cancelled) setClients(Array.isArray(list) ? list : []);
        })
        .catch(() => {
          if (!cancelled) setClients([]);
        });
      return () => {
        cancelled = true;
      };
    }
    const list = readSessionCurrentClient();
    setClients(Array.isArray(list) ? list : []);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const selectedYear = useSelector((state) => state.report.filter.summaryYear);
  const selectedMonth = useSelector((state) => state.report.filter.summaryMonth);
  const summaryRows = useSelector((state) => state.report.summaryRows);

  // The period lives in the report slice, so the panel reopens on the month it was last
  // generated for. Only the month and year are offered: the summary is a per-month
  // roll-up, so a fiscal week or an arbitrary date range has nothing to report against.
  const onPeriodChange = ({ month, year }) => {
    dispatch(reportActions.replaceSummaryMonth(parseInt(month, 10)));
    dispatch(reportActions.replaceSummaryYear(parseInt(year, 10)));
  };

  const btnClickHandler = () => {
    const startDate = getDateInFormat(getStartDateFromAnyMonth(selectedMonth, selectedYear));
    const endDate = getDateInFormat(getEndDateFromAnyMonth(selectedMonth, selectedYear));
    // "All" normally means "no filter", and the endpoint reads that as every client in the system.
    // For a BDM it has to mean every client of *theirs*, so their own ids are sent explicitly --
    // omitting them would report on accounts that are not theirs. A BDM with no accounts sends an
    // empty list rather than null, which is the same distinction.
    // @since NEXT_VERSION
    const bdmClientIds = clients.map((client) => client.id).filter((id) => id != null);
    const scopedClientIds =
      selectedClient === ALL_CLIENTS ? (isBdm ? bdmClientIds : null) : [selectedClient];
    loadEffortSummary(startDate, endDate, scopedClientIds)
      .then((data) => {
        dispatch(reportActions.replaceSummaryRows(data.data));
        dispatch(reportActions.replaceSummaryYear(selectedYear));
        dispatch(reportActions.replaceSummaryMonth(selectedMonth));
        setIsCalled(true);
      })
      .catch((error) => {
        handleAxiosError(error);
        setIsCalled(true);
      });
  };

  return (
    <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={1}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Effort Summary
              </MDTypography>
            </MDBox>
            <MDBox mt={3} p={2}>
              {/* No Member / Manager filter: every column is a per-client ratio, so
                  narrowing to one person would make each row read 1/1 or 0/1. */}
              <EffortFilterBar
                idPrefix="effort-summary"
                showClient
                clientValue={selectedClient}
                onClientChange={setSelectedClient}
                clientOptions={toClientListOptions(clients)}
                showOnlyMonthPeriod
                showMemberManagerDropdown={false}
                period={{ month: selectedMonth, year: selectedYear }}
                onPeriodChange={onPeriodChange}
                actionLabel="Generate"
                onAction={btnClickHandler}
              />
            </MDBox>
            {summaryRows.length > 0 && (
              <TableContainer sx={{ mx: 2, width: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell align="center">Submitted Members</TableCell>
                      <TableCell align="center">Approved Members</TableCell>
                      <TableCell align="center">Submitted Managers</TableCell>
                      <TableCell align="center">Approved Managers</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summaryRows.map((row) => {
                      let color = "success";
                      if (row.submittedTotalHours < row.expectedTotalHours / 2) color = "error";
                      else if (
                        row.submittedTotalHours >= row.expectedTotalHours / 2 &&
                        row.submittedTotalHours < row.expectedTotalHours
                      )
                        color = "warning";
                      return (
                        <TableRow key={row.clientId}>
                          <TableCell component="th" scope="row">
                            {row.clientName}
                          </TableCell>
                          <TableCell align="center">
                            <ProfileTooltip
                              title={
                                "Expected Hours: " +
                                row.expectedTotalHours +
                                ", Submitted Hours: " +
                                row.submittedTotalHours
                              }
                            >
                              <Chip
                                label={row.submittedMemberCount + "/" + row.totalMemberCount}
                                color={color}
                              />
                            </ProfileTooltip>
                          </TableCell>
                          <TableCell align="center">
                            <ProfileTooltip
                              title={
                                "Approved Hours: " +
                                row.approvedTotalHours +
                                ", Submitted Hours: " +
                                row.submittedTotalHours
                              }
                            >
                              <Chip label={row.approvedMemberCount + "/" + row.totalMemberCount} variant="outlined" />
                            </ProfileTooltip>
                          </TableCell>
                          <TableCell align="center">
                            <ProfileTooltip
                              title={"Submitted Hours: " + (row.submittedManagerHours ?? 0)}
                            >
                              <Chip
                                label={(row.submittedManagerCount ?? 0) + "/" + (row.totalManagerCount ?? 0)}
                                color="info"
                              />
                            </ProfileTooltip>
                          </TableCell>
                          <TableCell align="center">
                            <ProfileTooltip
                              title={"Approved Hours: " + (row.approvedManagerHours ?? 0)}
                            >
                              <Chip
                                label={(row.approvedManagerCount ?? 0) + "/" + (row.totalManagerCount ?? 0)}
                                variant="outlined"
                              />
                            </ProfileTooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {summaryRows.length === 0 && isCalled && (
              <MDBox mx={2}>
                <div style={{textAlign: "center", marginBottom: '10px'}}>Sorry! No Data Available</div>
              </MDBox>
            )}
    </Card>
  );
};

export default EffortSummaryView;
